import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import styles from "./Input.module.css";
import classNames from "classnames";
import PrevIcon from "../../assets/prev.svg";
import NextIcon from "../../assets/next.svg";
import CloseIcon from "../../assets/calendar-close.svg";
import { DateTime } from "luxon";
import ReactDatePicker from "react-datepicker";
import type { DatePickerProps } from "react-datepicker";

interface DateInputProps extends Omit<DatePickerProps, "onChange"> {
	selected?: Date | null;
	disabled?: boolean;
	name?: string;
	type?: "date" | "datetime";
	dateFormat?: string;
	placeholder?: string;
	required?: boolean;
	onChange?: any;
	minDate?: Date;
	filterDate?: (date: Date) => boolean;
}

const DateInput: React.FC<DateInputProps> = ({
	selected,
	disabled,
	name,
	type = "date",
	dateFormat = "dd-MM-yyyy",
	placeholder,
	required,
	onChange,
	minDate,
	filterDate,
}) => {
	const [value, setValue] = useState<Date | null>(selected ?? null);
	const defaultValue = useRef<Date | null>(null);
	const datePickerRef = useRef<ReactDatePicker>(null);

	const years = useMemo(
		() =>
			Array.from(
				{ length: new Date().getFullYear() + 10 - 1980 },
				(_, index) => 1980 + index
			),
		[]
	);

	useEffect(() => {
		setValue(selected ?? null);
	}, [selected]);

	const onCalendarOpen = useCallback(() => {
		defaultValue.current = value;
	}, [value]);

	const months = useMemo(
		() => [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		],
		[]
	);

	const onCancel = useCallback(
		(e: React.MouseEvent, reset = true) => {
			e.preventDefault();
			if (reset) {
				setValue(defaultValue.current);
				onChange?.({ name, value: defaultValue.current });
			}
			datePickerRef.current?.setOpen(false);
		},
		[onChange, name]
	);

	const handleChange = (selectedDate: Date | null) => {
		if (disabled) return;

		setValue(selectedDate);
		onChange?.({ name, value: selectedDate });
		datePickerRef.current?.setOpen(false);
	};

	return (
		<ReactDatePicker
			selected={value}
			minDate={minDate}
			onChange={handleChange}
			onCalendarOpen={onCalendarOpen}
			name={name}
			showTimeSelect={type === "datetime"}
			dateFormat={dateFormat}
			filterDate={filterDate ?? undefined}
			className={classNames(styles.input, {
				[styles.disabled]: !!disabled,
			})}
			placeholderText={
				placeholder
					? `${placeholder}${required ? "*" : ""}`
					: "dd-mm-yyyy"
			}
			isClearable
			weekDayClassName={() => styles.weekDay}
			required={required}
			disabled={disabled}
			shouldCloseOnSelect={false}
			calendarClassName={classNames(styles.calendar, {
				[styles.calendarDateTime]: type === "datetime",
			})}
			wrapperClassName={styles.wrapper}
			ref={datePickerRef}
			calendarStartDay={1}
			dayClassName={() => styles.day}
			monthClassName={() => styles.month}
			renderCustomHeader={({
				date,
				changeYear,
				changeMonth,
				decreaseMonth,
				increaseMonth,
				prevMonthButtonDisabled,
				nextMonthButtonDisabled,
			}: any) => (
				<>
					<div className={styles.headerTitleContainer}>
						<div>Choose date</div>
						<button
							type="button"
							onClick={(e) => onCancel(e, false)}
						>
							<img src={CloseIcon} alt="Close" />
						</button>
					</div>
					<div className={styles.pickerHeader}>
						<button
							onClick={decreaseMonth}
							className={styles.arrow}
							disabled={prevMonthButtonDisabled}
						>
							<img src={PrevIcon} alt="Previous" />
						</button>
						<div className={styles.yearMonthContainer}>
							<select
								className={styles.select}
								value={DateTime.fromJSDate(date).year}
								onChange={({ target: { value } }) =>
									changeYear(parseInt(value, 10))
								}
							>
								{years.map((year: any) => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>

							<select
								className={styles.select}
								value={
									months[DateTime.fromJSDate(date).month - 1]
								}
								onChange={({ target: { value } }) =>
									changeMonth(months.indexOf(value))
								}
							>
								{months.map((month, index) => (
									<option key={index} value={month}>
										{month}
									</option>
								))}
							</select>
						</div>

						<button
							onClick={increaseMonth}
							className={styles.arrow}
							disabled={nextMonthButtonDisabled}
						>
							<img src={NextIcon} alt="Next" />
						</button>
					</div>
				</>
			)}
		/>
	);
};

export default DateInput;
